import { useCallback } from 'react';

const BASE_PATH = '/api/v1/biotransac';

export interface TimerConfig {
  id?: number;
  code: string;
  libelle: string;
  valeurMinutes: number;
  actif: boolean;
}

export interface CodeOperation {
  id?: number;
  codeOpe: string;
  libelle: string;
  description: string;
  statut: string;
}

export type CreateTimerPayload = Omit<TimerConfig, 'id'>;
export type CreateCodeOperationPayload = Omit<CodeOperation, 'id'>;
export type PatchCodeOperationPayload = Partial<CreateCodeOperationPayload>;

function getCookieValue(name: string): string | null {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
}

function getAuthHeader(): string | null {
  const token =
    localStorage.getItem('authToken') ??
    localStorage.getItem('accessToken') ??
    localStorage.getItem('token');

  return token ? `Bearer ${token}` : null;
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const authHeader = getAuthHeader();
  const csrfToken = getCookieValue('XSRF-TOKEN');

  const response = await fetch(`${BASE_PATH}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}),
      ...init?.headers,
    },
  });

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message = typeof payload === 'string' ? payload : JSON.stringify(payload);
    throw new Error(message || `HTTP ${response.status}`);
  }

  return payload as T;
}

export function useBiotransacSettings() {
  const createTimer = useCallback(async (payload: CreateTimerPayload) => {
    return apiRequest<TimerConfig>('/timers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }, []);

  const getAllTimers = useCallback(async () => {
    return apiRequest<TimerConfig[]>('/timers');
  }, []);

  const getTimerByCode = useCallback(async (code: string) => {
    return apiRequest<TimerConfig>(`/timers/code/${encodeURIComponent(code)}`);
  }, []);

  const patchTimer = useCallback(
    async (id: number, payload: Partial<CreateTimerPayload>) => {
      return apiRequest<TimerConfig>(`/timers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
    []
  );

  const deleteTimer = useCallback(async (id: number) => {
    return apiRequest<void>(`/timers/${id}`, {
      method: 'DELETE',
    });
  }, []);

  const createCodeOperation = useCallback(async (payload: CreateCodeOperationPayload) => {
    return apiRequest<CodeOperation>('/code-operations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }, []);

  const getAllCodeOperations = useCallback(async () => {
    return apiRequest<CodeOperation[]>('/code-operations');
  }, []);

  const patchCodeOperation = useCallback(
    async (id: number, payload: PatchCodeOperationPayload) => {
      return apiRequest<CodeOperation>(`/code-operations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
    []
  );

  const deleteCodeOperation = useCallback(async (id: number) => {
    return apiRequest<void>(`/code-operations/${id}`, {
      method: 'DELETE',
    });
  }, []);

  return {
    // Timers
    createTimer,
    getAllTimers,
    getTimerByCode,
    patchTimer,
    deleteTimer,

    // Code Operations
    createCodeOperation,
    getAllCodeOperations,
    patchCodeOperation,
    deleteCodeOperation,
  };
}
